import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  CompareMode,
  DatePreset,
  SourceKey,
  SourceState,
} from "./shared/contracts";

export type LanguageCode = "es" | "en";

export const LANGUAGE_OPTIONS: Array<{ value: LanguageCode; label: string }> = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
];

interface Dictionary {
  locale: string;
  language: {
    label: string;
  };
  nav: {
    overview: string;
    organic: string;
    paid: string;
    conversions: string;
    technical: string;
    devices: string;
    users: string;
  };
  filters: {
    urlToAnalyze: string;
    analyze: string;
    urlMode: string;
    exactUrl: string;
    urlContaining: string;
    urlNotContaining: string;
    queryPlaceholder: string;
    countryPlaceholder: string;
    device: string;
    allDevices: string;
    mobile: string;
    desktop: string;
    tablet: string;
    openNavigation: string;
    closeSession: string;
    recentSearches: string;
  };
  date: {
    dialogLabel: string;
    presetsLabel: string;
    previousMonth: string;
    nextMonth: string;
    start: string;
    end: string;
    compare: string;
    mode: string;
    compareStart: string;
    compareEnd: string;
    compareRange: string;
    invalidDates: string;
    cancel: string;
    apply: string;
    applyFilters: string;
    maxHistory: string;
    swappedNotice: string;
    vs: string;
    weekdays: string[];
    presets: Record<DatePreset, string>;
    compareModes: Record<CompareMode, string>;
  };
  sources: {
    heading: string;
    labels: Record<SourceKey, string>;
    states: Record<SourceState, string>;
  };
  dashboard: {
    eyebrow: string;
    urlHealth: string;
    sessions: string;
    organicClicks: string;
    paidClicks: string;
    conversions: string;
    performance: string;
    siteWideView: string;
    landingPage: string;
    updated: string;
    dataUrl: string;
    refreshSources: string;
    analysisErrorTitle: string;
    firstRunTitle: string;
    firstRunBody: string;
    noComparison: string;
  };
  overview: {
    trafficConversions: string;
    noGa4Activity: string;
    acquisitionChannels: string;
    noChannels: string;
    firstUserSourceMediumTitle: string;
    firstUserSourceMediumMeta: string;
    firstUserSourceMediumTable: string;
    sourceMedium: string;
    conversionRate: string;
    previousConversions: string;
    noFirstUserSourceMedium: string;
    searchVisibilitySummary: string;
    noSearchVisibilitySummary: string;
    paidMediaSummary: string;
    noPaidMediaSummary: string;
    landingBehavior: string;
    noClaritySignals: string;
    scrollDepth: string;
    deadClicks: string;
    rageClicks: string;
    quickbacks: string;
    scriptErrors: string;
  };
  organic: {
    googleClicks: string;
    impressions: string;
    averagePosition: string;
    googleQueries: string;
    organicPerformance: string;
    noGscDailySeries: string;
    indexation: string;
    verdict: string;
    coverage: string;
    lastCrawl: string;
    inspectedUrl: string;
    organicQueries: string;
    query: string;
    country: string;
    device: string;
    searchAppearance: string;
    day: string;
    source: string;
    clicks: string;
    impr: string;
    pos: string;
    noQueries: string;
    noBreakdownRows: string;
    noBreakdownAvailable: string;
    tabs: {
      queries: string;
      countries: string;
      devices: string;
      searchAppearance: string;
      days: string;
    };
    microsoftOrganicSearch: string;
    microsoftOrganicMeta: string;
    bingClicks: string;
    bingImpressions: string;
    bingCtr: string;
    bingPosition: string;
    bingQueries: string;
    inboundAnchors: string;
    bingPerformance: string;
    bingMeta: string;
    noBingDailySeries: string;
    bingStatus: string;
    recognized: string;
    pageRecognized: string;
    notRecognized: string;
    discovered: string;
    document: string;
    noBingQueries: string;
  };
  paid: {
    impressions: string;
    cost: string;
    cpa: string;
    investmentResults: string;
    noLinkedCampaigns: string;
    efficiency: string;
    avgCpc: string;
    convValue: string;
    campaigns: string;
    landingCampaigns: string;
    campaign: string;
    linkedCampaignCountries: string;
    linkedCampaignCountriesMeta: string;
    countryAttributionHelp: string;
    confidence: string;
    urlShare: string;
    noCountryAttribution: string;
    confidenceLabels: {
      exact: string;
      high: string;
      estimated: string;
    };
    noPaidActivity: string;
    microsoftPaidSearch: string;
    microsoftPaidMeta: string;
    microsoftClicks: string;
    noMicrosoftAdsActivity: string;
    microsoftPendingMetaError: string;
    microsoftPendingMetaOauth: string;
    microsoftPendingTitle: string;
  };
  conversions: {
    filterLabel: string;
    filterHelp: string;
    origin: string;
    origins: Record<"all" | "google" | "microsoft" | "paid" | "organic" | "other", string>;
    ecosystems: Record<"google" | "microsoft" | "other", string>;
    keyEvents: string;
    paidKeyEvents: string;
    organicKeyEvents: string;
    otherKeyEvents: string;
    googleAdsConversions: string;
    microsoftAdsConversions: string;
    googleAds: string;
    microsoftAds: string;
    trendTitle: string;
    trendMeta: string;
    summaryTitle: string;
    summaryMeta: string;
    sessions: string;
    rate: string;
    adsConversions: string;
    adsValue: string;
    sourceBreakdown: string;
    ga4Meta: string;
    sourceMedium: string;
    selectAllSources: string;
    deselectAllSources: string;
    selectSource: string;
    previous: string;
    adsBreakdown: string;
    adsMeta: string;
    source: string;
    clicks: string;
    impressions: string;
    cost: string;
    cpa: string;
    roas: string;
    noTrend: string;
    noGa4Data: string;
    noAdsData: string;
    note: string;
  };
  technical: {
    seoScore: string;
    historicalCoreWebVitals: string;
    lastSixMonths: string;
    noCruxHistory: string;
    accessibility: string;
    bestPractices: string;
    technicalSeo: string;
    liveInspection: string;
    httpStatus: string;
    canonicalNotDetected: string;
    robotsNoRestrictions: string;
    schemaNotDetected: string;
    responseTime: string;
    microsoftCrawlIndex: string;
    noPageSpeedEquivalent: string;
    anchors: string;
    auditHistory: string;
    snapshots: string;
    futureAnalyses: string;
    priorityActions: string;
    reviewOpportunity: string;
    noCriticalOpportunities: string;
  };
  admin: {
    accessControl: string;
    authorizedUsers: string;
    invitationsHelp: string;
    users: string;
    directory: string;
    directoryMeta: string;
    user: string;
    role: string;
    status: string;
    passkeys: string;
    lastAccess: string;
    viewer: string;
    admin: string;
    active: string;
    disabled: string;
    invited: string;
    renewInvitation: string;
    enable: string;
    disable: string;
    inviteUser: string;
    inviteMeta: string;
    name: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    generating: string;
    createInvitation: string;
    oneTimeLink: string;
    copyInvitation: string;
    never: string;
  };
  devices: {
    personalAccess: string;
    myDevices: string;
    help: string;
    deletionNote: string;
    registeredPasskeys: string;
    synced: string;
    local: string;
    created: string;
    lastUse: string;
    empty: string;
    addDevice: string;
    addMeta: string;
    linkHelp: string;
    generateLink: string;
    generating: string;
    oneTimeLink: string;
    expires: string;
    copyLink: string;
    linkReady: string;
    singleDevice: string;
    multiDevice: string;
    never: string;
    fallbackError: string;
    remove: string;
    removing: string;
    confirmDelete: string;
    confirmDeleteLast: string;
    removed: string;
  };
  access: {
    newDevice: string;
    secureInvitation: string;
    addPasskey: string;
    createPasskey: string;
    validatingLink: string;
    deviceName: string;
    devicePlaceholder: string;
    creatingPasskey: string;
    createAndEnter: string;
    privateAccess: string;
    loginHelp: string;
    verifying: string;
    passkeyLogin: string;
    emailCodeFallback: string;
    emailCodeHelp: string;
    email: string;
    emailPlaceholder: string;
    sendingCode: string;
    sendCode: string;
    emailCodeSent: string;
    emailCodeVerifyHelp: string;
    code: string;
    verifyingCode: string;
    verifyCode: string;
    requestAnotherCode: string;
    backToPasskey: string;
    emailCodeError: string;
    unsupported: string;
    secureFoot: string;
    linkError: string;
    cancelled: string;
    authenticatorError: string;
    authenticatorRegistrationError: string;
    genericPasskeyError: string;
  };
}

const es: Dictionary = {
  locale: "es-ES",
  language: {
    label: "Idioma",
  },
  nav: {
    overview: "Resumen URL",
    organic: "Búsqueda orgánica",
    paid: "Búsqueda pagada",
    conversions: "Conversiones",
    technical: "SEO técnico",
    devices: "Mis dispositivos",
    users: "Usuarios",
  },
  filters: {
    urlToAnalyze: "URL a analizar",
    analyze: "Analizar",
    urlMode: "URL",
    exactUrl: "URL exacta",
    urlContaining: "URL contiene",
    urlNotContaining: "URL no contiene",
    queryPlaceholder: "Query",
    countryPlaceholder: "País",
    device: "Dispositivo",
    allDevices: "Todos",
    mobile: "Móvil",
    desktop: "Desktop",
    tablet: "Tablet",
    openNavigation: "Abrir navegación",
    closeSession: "Cerrar sesión",
    recentSearches: "Últimas consultas",
  },
  date: {
    dialogLabel: "Selector de fechas",
    presetsLabel: "Presets de fechas",
    previousMonth: "Mes anterior",
    nextMonth: "Mes siguiente",
    start: "Inicio",
    end: "Fin",
    compare: "Comparar",
    mode: "Modo",
    compareStart: "Inicio comparación",
    compareEnd: "Fin comparación",
    compareRange: "Rango de comparación",
    invalidDates: "Fechas no válidas",
    cancel: "Cancelar",
    apply: "Aplicar",
    applyFilters: "Aplicar filtros",
    maxHistory: "Histórico de máximo 2 años",
    swappedNotice: "Intercambié las fechas para mantener inicio antes de fin.",
    vs: "vs",
    weekdays: ["L", "M", "X", "J", "V", "S", "D"],
    presets: {
      today: "Hoy",
      yesterday: "Ayer",
      last_7_days: "Últimos 7 días",
      last_28_days: "Últimos 28 días",
      last_30_days: "Últimos 30 días",
      last_90_days: "Últimos 90 días",
      this_week: "Esta semana",
      last_week: "Semana pasada",
      this_month: "Este mes",
      last_month: "Mes pasado",
      quarter_to_date: "Trimestre hasta la fecha",
      this_year: "Este año",
      previous_year: "Año anterior",
      custom: "Personalizado",
    },
    compareModes: {
      previous_period: "Período anterior",
      same_period_last_year: "Mismo período del año anterior",
      custom: "Personalizado",
    },
  },
  sources: {
    heading: "Fuentes",
    labels: {
      technical: "SEO técnico",
      pagespeed: "PageSpeed",
      bing_webmaster: "Bing Webmaster",
      clarity: "Microsoft Clarity",
      ga4: "Google Analytics 4",
      search_console: "Search Console",
      google_ads: "Google Ads",
      microsoft_ads: "Microsoft Ads",
      crux: "Chrome UX Report",
      ai_performance: "Rendimiento IA",
    },
    states: {
      connected: "Conectado",
      missing_credentials: "Sin autorización",
      limited: "Limitado",
      error: "Error",
    },
  },
  dashboard: {
    eyebrow: "Inteligencia URL",
    urlHealth: "Salud URL",
    sessions: "Sesiones",
    organicClicks: "Clics orgánicos",
    paidClicks: "Clics pagados",
    conversions: "Conversiones",
    performance: "Performance",
    siteWideView: "Vista global del sitio",
    landingPage: "Landing page",
    updated: "Actualizado",
    dataUrl: "Datos consultados para:",
    refreshSources: "Actualizar fuentes",
    analysisErrorTitle: "No se pudo completar el análisis",
    firstRunTitle: "Escribe una URL para abrir su vista 360°",
    firstRunBody:
      "El primer análisis combinará diagnóstico técnico, PageSpeed, Bing y las fuentes que estén autorizadas.",
    noComparison: "sin comparativa",
  },
  overview: {
    trafficConversions: "Tráfico y conversiones",
    noGa4Activity: "GA4 no devolvió actividad para esta URL y rango",
    acquisitionChannels: "Canales de adquisición",
    noChannels: "Sin desglose de canales",
    firstUserSourceMediumTitle:
      "Evolución de conversiones por fuente/medio inicial",
    firstUserSourceMediumMeta: "GA4 · First user source / medium",
    firstUserSourceMediumTable: "Fuente/medio inicial",
    sourceMedium: "Fuente / medio",
    conversionRate: "Tasa conv.",
    previousConversions: "Conv. ant.",
    noFirstUserSourceMedium:
      "GA4 no devolvió fuente/medio inicial para esta URL y rango",
    searchVisibilitySummary: "Resumen de visibilidad orgánica",
    noSearchVisibilitySummary:
      "Google y Bing no devolvieron visibilidad orgánica para esta URL y rango",
    paidMediaSummary: "Resumen de tráfico pagado",
    noPaidMediaSummary:
      "Google Ads y Microsoft Advertising no devolvieron actividad para esta URL y rango",
    landingBehavior: "Comportamiento de la landing",
    noClaritySignals: "Clarity no devolvió señales para esta URL",
    scrollDepth: "Profundidad de scroll",
    deadClicks: "Dead clicks",
    rageClicks: "Rage clicks",
    quickbacks: "Quickbacks",
    scriptErrors: "Errores de script",
  },
  organic: {
    googleClicks: "Clics de Google",
    impressions: "Impresiones",
    averagePosition: "Posición media",
    googleQueries: "Queries de Google",
    organicPerformance: "Rendimiento orgánico",
    noGscDailySeries: "Search Console no devolvió serie diaria",
    indexation: "Indexación",
    verdict: "Veredicto",
    coverage: "Cobertura",
    lastCrawl: "Último rastreo",
    inspectedUrl: "URL inspeccionada",
    organicQueries: "Queries orgánicas",
    query: "Query",
    country: "País",
    device: "Dispositivo",
    searchAppearance: "Search Appearance",
    day: "Día",
    source: "Fuente",
    clicks: "Clics",
    impr: "Impr.",
    pos: "Pos.",
    noQueries: "Sin queries para los filtros seleccionados",
    noBreakdownRows: "Sin datos para esta dimensión y los filtros seleccionados",
    noBreakdownAvailable:
      "Esta dimensión no está disponible en la API conectada.",
    tabs: {
      queries: "Queries",
      countries: "Countries",
      devices: "Devices",
      searchAppearance: "Search Appearance",
      days: "Days",
    },
    microsoftOrganicSearch: "Búsqueda orgánica en Microsoft",
    microsoftOrganicMeta: "Bing Webmaster · misma URL y rango",
    bingClicks: "Clics de Bing",
    bingImpressions: "Impresiones de Bing",
    bingCtr: "CTR de Bing",
    bingPosition: "Posición en Bing",
    bingQueries: "Queries de Bing",
    inboundAnchors: "Anchors entrantes",
    bingPerformance: "Rendimiento orgánico en Bing",
    bingMeta: "URL, fecha y query · País y dispositivo no disponibles",
    noBingDailySeries: "Bing no devolvió serie diaria para el rango",
    bingStatus: "Estado en Bing",
    recognized: "Reconocida",
    pageRecognized: "Página reconocida",
    notRecognized: "No reconocida",
    discovered: "Descubierta",
    document: "Documento",
    noBingQueries: "Bing no devolvió queries para los filtros",
  },
  paid: {
    impressions: "Impresiones",
    cost: "Coste",
    cpa: "CPA",
    investmentResults: "Inversión y resultados",
    noLinkedCampaigns: "No hay campañas vinculadas a esta URL y rango",
    efficiency: "Eficiencia",
    avgCpc: "CPC medio",
    convValue: "Valor conv.",
    campaigns: "Campañas",
    landingCampaigns: "Campañas de la landing",
    campaign: "Campaña",
    linkedCampaignCountries: "Países de campañas vinculadas",
    linkedCampaignCountriesMeta:
      "atribución por campañas que enviaron tráfico a esta URL",
    countryAttributionHelp:
      "Exacto si las campañas están dedicadas a esta landing; si comparten varias URLs, se estima distribuyendo el rendimiento geográfico de la campaña según el peso de esta URL.",
    confidence: "Confianza",
    urlShare: "Peso URL",
    noCountryAttribution:
      "La plataforma no devolvió desglose geográfico para las campañas vinculadas",
    confidenceLabels: {
      exact: "Exacto",
      high: "Alta",
      estimated: "Estimado",
    },
    noPaidActivity: "La URL no tiene actividad pagada en el período",
    microsoftPaidSearch: "Búsqueda pagada en Microsoft",
    microsoftPaidMeta: "Microsoft Advertising · misma landing y rango",
    microsoftClicks: "Clics de Microsoft",
    noMicrosoftAdsActivity: "Sin actividad de Microsoft Ads para la URL",
    microsoftPendingMetaError: "Error de conexión",
    microsoftPendingMetaOauth: "OAuth pendiente",
    microsoftPendingTitle:
      "Faltan client ID, client secret, refresh token, developer token y account IDs",
  },
  conversions: {
    filterLabel: "Origen de la conversión",
    filterHelp: "Combina Key Events de GA4 y conversiones de Ads",
    origin: "Origen",
    origins: {
      all: "Todos los orígenes",
      google: "Google",
      microsoft: "Microsoft / Bing",
      paid: "Todo el tráfico pagado",
      organic: "Tráfico orgánico",
      other: "Directo, referral y otros",
    },
    ecosystems: {
      google: "Google",
      microsoft: "Microsoft",
      other: "Otros",
    },
    keyEvents: "Key Events",
    paidKeyEvents: "Key Events pagados",
    organicKeyEvents: "Key Events orgánicos",
    otherKeyEvents: "Key Events de otros orígenes",
    googleAdsConversions: "Conversiones Google Ads",
    microsoftAdsConversions: "Conversiones Microsoft Ads",
    googleAds: "Google Ads",
    microsoftAds: "Microsoft Ads",
    trendTitle: "Evolución de Key Events y Ads",
    trendMeta: "GA4 + Google Ads + Microsoft Ads",
    summaryTitle: "Resumen del origen seleccionado",
    summaryMeta: "Métricas del período y filtros actuales",
    sessions: "Sesiones",
    rate: "Tasa de Key Events",
    adsConversions: "Conversiones Ads",
    adsValue: "Valor de conversiones Ads",
    sourceBreakdown: "Key Events por fuente / medio",
    ga4Meta: "Google Analytics 4 · First user source / medium",
    sourceMedium: "Fuente / medio",
    selectAllSources: "Seleccionar todas las fuentes",
    deselectAllSources: "Deseleccionar todas las fuentes",
    selectSource: "Seleccionar fuente",
    previous: "Período anterior",
    adsBreakdown: "Conversiones por plataforma",
    adsMeta: "Google Ads + Microsoft Advertising",
    source: "Fuente",
    clicks: "Clics",
    impressions: "Impresiones",
    cost: "Coste",
    cpa: "CPA",
    roas: "ROAS",
    noTrend: "No hay serie de conversiones para los filtros seleccionados",
    noGa4Data: "GA4 no devolvió Key Events por fuente para este rango",
    noAdsData: "No hay conversiones de Ads para este origen y rango",
    note:
      "GA4 muestra Key Events por primera fuente / medio. Google Ads y Microsoft Ads muestran conversiones atribuidas por sus propias plataformas; se mantienen separadas para evitar doble conteo.",
  },
  technical: {
    seoScore: "SEO score",
    historicalCoreWebVitals: "Core Web Vitals históricos",
    lastSixMonths: "últimos 6 meses",
    noCruxHistory: "CrUX no tiene histórico suficiente para esta URL",
    accessibility: "Accesibilidad",
    bestPractices: "Buenas prácticas",
    technicalSeo: "SEO técnico",
    liveInspection: "Inspección live",
    httpStatus: "HTTP status",
    canonicalNotDetected: "No detectada",
    robotsNoRestrictions: "Sin restricciones",
    schemaNotDetected: "No detectado",
    responseTime: "Tiempo de respuesta",
    microsoftCrawlIndex: "Rastreo e índice en Microsoft",
    noPageSpeedEquivalent: "Bing Webmaster · sin equivalente PageSpeed",
    anchors: "Anchors",
    auditHistory: "Histórico de auditorías",
    snapshots: "snapshots",
    futureAnalyses: "Los próximos análisis crearán esta serie",
    priorityActions: "Acciones prioritarias",
    reviewOpportunity: "Revisar oportunidad",
    noCriticalOpportunities: "No hay oportunidades críticas.",
  },
  admin: {
    accessControl: "Control de acceso",
    authorizedUsers: "Usuarios autorizados",
    invitationsHelp:
      "Cada usuario accede con su propia Passkey. Las invitaciones son de un solo uso y caducan en siete días.",
    users: "usuarios",
    directory: "Directorio",
    directoryMeta: "Roles, estado y Passkeys registradas",
    user: "Usuario",
    role: "Rol",
    status: "Estado",
    passkeys: "Passkeys",
    lastAccess: "Último acceso",
    viewer: "Viewer",
    admin: "Admin",
    active: "Activo",
    disabled: "Desactivado",
    invited: "Invitado",
    renewInvitation: "Generar nueva invitación",
    enable: "Activar",
    disable: "Desactivar",
    inviteUser: "Invitar usuario",
    inviteMeta: "Genera un enlace de registro Passkey",
    name: "Nombre",
    namePlaceholder: "Nombre del usuario",
    emailPlaceholder: "usuario@empresa.com",
    generating: "Generando...",
    createInvitation: "Crear invitación",
    oneTimeLink: "Enlace de un solo uso",
    copyInvitation: "Copiar invitación",
    never: "Nunca",
  },
  devices: {
    personalAccess: "Acceso personal",
    myDevices: "Mis dispositivos",
    help:
      "Registra una Passkey por cada navegador, dispositivo o llave física desde donde quieras entrar al Dashboard.",
    registeredPasskeys: "Passkeys registradas",
    synced: "sincronizada",
    local: "local",
    created: "Creada",
    lastUse: "Último uso",
    empty: "No hay Passkeys registradas para esta cuenta.",
    addDevice: "Agregar dispositivo",
    addMeta: "Genera un enlace de un solo uso",
    linkHelp:
      "Copia el enlace y ábrelo en el móvil, portátil, navegador o llave física donde quieras guardar la nueva Passkey.",
    generateLink: "Generar enlace",
    generating: "Generando...",
    oneTimeLink: "Enlace de un solo uso",
    expires: "caduca",
    copyLink: "Copiar enlace",
    linkReady: "Enlace listo para copiar.",
    singleDevice: "Dispositivo único",
    multiDevice: "Multidispositivo",
    deletionNote:
      "Puedes quitar aquí una Passkey del Dashboard. Esto no borra la copia guardada en el gestor ni cierra sesiones que ya estén abiertas.",
    remove: "Eliminar",
    removing: "Eliminando...",
    confirmDelete:
      "¿Eliminar la Passkey «{name}» del Dashboard? Dejará de servir para futuros accesos, pero no se borrará del gestor del dispositivo ni cerrará sesiones abiertas.",
    confirmDeleteLast:
      "Esta es tu última Passkey registrada. ¿Eliminar «{name}»? Para volver a entrar tendrás que usar un código por email o registrar otra Passkey. La copia local y las sesiones abiertas no se borrarán ni cerrarán.",
    removed: "Passkey eliminada del Dashboard.",
    never: "Nunca",
    fallbackError: "No se pudo generar el enlace.",
  },
  access: {
    newDevice: "Nuevo dispositivo",
    secureInvitation: "Invitación segura",
    addPasskey: "Agrega tu Passkey",
    createPasskey: "Crea tu Passkey",
    validatingLink: "Validando el enlace...",
    deviceName: "Nombre del dispositivo",
    devicePlaceholder: "Portátil, móvil o llave física",
    creatingPasskey: "Creando Passkey...",
    createAndEnter: "Crear Passkey y acceder",
    privateAccess: "Acceso privado",
    loginHelp:
      "Usa la Passkey guardada en tu dispositivo, gestor de claves o llave de seguridad.",
    verifying: "Verificando...",
    passkeyLogin: "Acceder con Passkey",
    emailCodeFallback: "No puedo usar mi Passkey",
    emailCodeHelp:
      "Introduce tu email registrado y te enviaremos un codigo temporal de acceso.",
    email: "Email registrado",
    emailPlaceholder: "tu@email.com",
    sendingCode: "Enviando codigo...",
    sendCode: "Enviar codigo",
    emailCodeSent:
      "Si el email pertenece a un usuario activo, recibiras un codigo temporal.",
    emailCodeVerifyHelp:
      "Introduce el codigo de 6 digitos que hemos enviado a tu email registrado.",
    code: "Codigo",
    verifyingCode: "Verificando codigo...",
    verifyCode: "Acceder con codigo",
    requestAnotherCode: "Solicitar otro codigo",
    backToPasskey: "Volver a Passkey",
    emailCodeError: "No se pudo completar el acceso por codigo.",
    unsupported: "Este navegador no admite Passkeys/WebAuthn.",
    secureFoot: "Sin contraseñas. Acceso cifrado y revocable.",
    linkError: "No se pudo abrir el enlace.",
    cancelled: "La operación fue cancelada o superó el tiempo disponible.",
    authenticatorError:
      "El autenticador no pudo firmar el acceso. Comprueba que estás usando el mismo perfil del navegador y el dispositivo o gestor donde guardaste esta Passkey. Para entrar ahora, pulsa «No puedo usar mi Passkey» y solicita un código por email; después podrás registrar otra Passkey desde Mi dispositivo.",
    authenticatorRegistrationError:
      "El navegador o gestor de Passkeys rechazó la creación antes de enviarla al servidor. Comprueba que estás en el dominio oficial, desbloquea el gestor de credenciales y vuelve a intentarlo. Si ya tienes una PassKey de esta cuenta en ese gestor, usa esa PassKey o prueba otro dispositivo o gestor. Detalle técnico:",
    genericPasskeyError: "No se pudo completar la operación con la Passkey.",
  },
};

const en: Dictionary = {
  locale: "en-US",
  language: {
    label: "Language",
  },
  nav: {
    overview: "URL Overview",
    organic: "Organic Search",
    paid: "Paid Search",
    conversions: "Conversions",
    technical: "Technical SEO",
    devices: "My devices",
    users: "Users",
  },
  filters: {
    urlToAnalyze: "URL to analyze",
    analyze: "Analyze",
    urlMode: "URL",
    exactUrl: "Exact URL",
    urlContaining: "URL containing",
    urlNotContaining: "URL not containing",
    queryPlaceholder: "Query",
    countryPlaceholder: "Country",
    device: "Device",
    allDevices: "All devices",
    mobile: "Mobile",
    desktop: "Desktop",
    tablet: "Tablet",
    openNavigation: "Open navigation",
    closeSession: "Sign out",
    recentSearches: "Recent searches",
  },
  date: {
    dialogLabel: "Date range picker",
    presetsLabel: "Date presets",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    start: "Start",
    end: "End",
    compare: "Compare",
    mode: "Mode",
    compareStart: "Compare start",
    compareEnd: "Compare end",
    compareRange: "Compare range",
    invalidDates: "Invalid dates",
    cancel: "Cancel",
    apply: "Apply",
    applyFilters: "Apply filters",
    maxHistory: "Maximum 2-year history",
    swappedNotice: "Dates were swapped to keep start before end.",
    vs: "vs",
    weekdays: ["M", "T", "W", "T", "F", "S", "S"],
    presets: {
      today: "Today",
      yesterday: "Yesterday",
      last_7_days: "Last 7 days",
      last_28_days: "Last 28 days",
      last_30_days: "Last 30 days",
      last_90_days: "Last 90 days",
      this_week: "This week",
      last_week: "Last week",
      this_month: "This month",
      last_month: "Last month",
      quarter_to_date: "Quarter to date",
      this_year: "This year",
      previous_year: "Previous year",
      custom: "Custom",
    },
    compareModes: {
      previous_period: "Previous period",
      same_period_last_year: "Same period last year",
      custom: "Custom",
    },
  },
  sources: {
    heading: "Sources",
    labels: {
      technical: "Technical SEO",
      pagespeed: "PageSpeed",
      bing_webmaster: "Bing Webmaster",
      clarity: "Microsoft Clarity",
      ga4: "Google Analytics 4",
      search_console: "Search Console",
      google_ads: "Google Ads",
      microsoft_ads: "Microsoft Ads",
      crux: "Chrome UX Report",
      ai_performance: "AI Performance",
    },
    states: {
      connected: "Connected",
      missing_credentials: "Not authorized",
      limited: "Limited",
      error: "Error",
    },
  },
  dashboard: {
    eyebrow: "URL Intelligence",
    urlHealth: "URL Health",
    sessions: "Sessions",
    organicClicks: "Organic clicks",
    paidClicks: "Paid clicks",
    conversions: "Conversions",
    performance: "Performance",
    siteWideView: "Site-wide view",
    landingPage: "Landing page",
    updated: "Updated",
    dataUrl: "Data queried for:",
    refreshSources: "Refresh sources",
    analysisErrorTitle: "The analysis could not be completed",
    firstRunTitle: "Enter a URL to open its 360° view",
    firstRunBody:
      "The first analysis will combine technical diagnostics, PageSpeed, Bing and the sources currently authorized.",
    noComparison: "no comparison",
  },
  overview: {
    trafficConversions: "Traffic and conversions",
    noGa4Activity: "GA4 returned no activity for this URL and range",
    acquisitionChannels: "Acquisition channels",
    noChannels: "No channel breakdown",
    firstUserSourceMediumTitle:
      "Conversion trend by first user source / medium",
    firstUserSourceMediumMeta: "GA4 · First user source / medium",
    firstUserSourceMediumTable: "First user source / medium",
    sourceMedium: "Source / medium",
    conversionRate: "Conv. rate",
    previousConversions: "Prev. conv.",
    noFirstUserSourceMedium:
      "GA4 returned no first user source / medium data for this URL and range",
    searchVisibilitySummary: "Organic visibility summary",
    noSearchVisibilitySummary:
      "Google and Bing returned no organic visibility for this URL and range",
    paidMediaSummary: "Paid traffic summary",
    noPaidMediaSummary:
      "Google Ads and Microsoft Advertising returned no activity for this URL and range",
    landingBehavior: "Landing page behavior",
    noClaritySignals: "Clarity returned no signals for this URL",
    scrollDepth: "Scroll depth",
    deadClicks: "Dead clicks",
    rageClicks: "Rage clicks",
    quickbacks: "Quickbacks",
    scriptErrors: "Script errors",
  },
  organic: {
    googleClicks: "Google clicks",
    impressions: "Impressions",
    averagePosition: "Average position",
    googleQueries: "Google queries",
    organicPerformance: "Organic performance",
    noGscDailySeries: "Search Console returned no daily series",
    indexation: "Indexation",
    verdict: "Verdict",
    coverage: "Coverage",
    lastCrawl: "Last crawl",
    inspectedUrl: "Inspected URL",
    organicQueries: "Organic queries",
    query: "Query",
    country: "Country",
    device: "Device",
    searchAppearance: "Search Appearance",
    day: "Day",
    source: "Source",
    clicks: "Clicks",
    impr: "Impr.",
    pos: "Pos.",
    noQueries: "No queries for the selected filters",
    noBreakdownRows: "No data for this dimension and the selected filters",
    noBreakdownAvailable:
      "This dimension is not available in the connected API.",
    tabs: {
      queries: "Queries",
      countries: "Countries",
      devices: "Devices",
      searchAppearance: "Search Appearance",
      days: "Days",
    },
    microsoftOrganicSearch: "Microsoft organic search",
    microsoftOrganicMeta: "Bing Webmaster · same URL and range",
    bingClicks: "Bing clicks",
    bingImpressions: "Bing impressions",
    bingCtr: "Bing CTR",
    bingPosition: "Bing position",
    bingQueries: "Bing queries",
    inboundAnchors: "Inbound anchors",
    bingPerformance: "Organic performance in Bing",
    bingMeta: "URL, date and query · Country and Device unavailable",
    noBingDailySeries: "Bing returned no daily series for the range",
    bingStatus: "Bing status",
    recognized: "Recognized",
    pageRecognized: "Page recognized",
    notRecognized: "Not recognized",
    discovered: "Discovered",
    document: "Document",
    noBingQueries: "Bing returned no queries for the filters",
  },
  paid: {
    impressions: "Impressions",
    cost: "Cost",
    cpa: "CPA",
    investmentResults: "Spend and results",
    noLinkedCampaigns: "No campaigns are linked to this URL and range",
    efficiency: "Efficiency",
    avgCpc: "Avg. CPC",
    convValue: "Conv. value",
    campaigns: "Campaigns",
    landingCampaigns: "Landing page campaigns",
    campaign: "Campaign",
    linkedCampaignCountries: "Countries from linked campaigns",
    linkedCampaignCountriesMeta:
      "attribution from campaigns that sent traffic to this URL",
    countryAttributionHelp:
      "Exact when campaigns are dedicated to this landing page; if campaigns share several URLs, the dashboard estimates by distributing campaign geo performance according to this URL's weight.",
    confidence: "Confidence",
    urlShare: "URL share",
    noCountryAttribution:
      "The platform returned no geo breakdown for the linked campaigns",
    confidenceLabels: {
      exact: "Exact",
      high: "High",
      estimated: "Estimated",
    },
    noPaidActivity: "The URL has no paid activity in the period",
    microsoftPaidSearch: "Microsoft paid search",
    microsoftPaidMeta: "Microsoft Advertising · same landing page and range",
    microsoftClicks: "Microsoft clicks",
    noMicrosoftAdsActivity: "No Microsoft Ads activity for this URL",
    microsoftPendingMetaError: "Connection error",
    microsoftPendingMetaOauth: "OAuth pending",
    microsoftPendingTitle:
      "Missing client ID, client secret, refresh token, developer token and account IDs",
  },
  conversions: {
    filterLabel: "Conversion origin",
    filterHelp: "Combine GA4 Key Events with Ads conversions",
    origin: "Origin",
    origins: {
      all: "All origins",
      google: "Google",
      microsoft: "Microsoft / Bing",
      paid: "All paid traffic",
      organic: "Organic traffic",
      other: "Direct, referral and other",
    },
    ecosystems: {
      google: "Google",
      microsoft: "Microsoft",
      other: "Other",
    },
    keyEvents: "Key Events",
    paidKeyEvents: "Paid Key Events",
    organicKeyEvents: "Organic Key Events",
    otherKeyEvents: "Other-origin Key Events",
    googleAdsConversions: "Google Ads conversions",
    microsoftAdsConversions: "Microsoft Ads conversions",
    googleAds: "Google Ads",
    microsoftAds: "Microsoft Ads",
    trendTitle: "Key Events and Ads trend",
    trendMeta: "GA4 + Google Ads + Microsoft Ads",
    summaryTitle: "Selected origin summary",
    summaryMeta: "Metrics for the current range and filters",
    sessions: "Sessions",
    rate: "Key Event rate",
    adsConversions: "Ads conversions",
    adsValue: "Ads conversion value",
    sourceBreakdown: "Key Events by source / medium",
    ga4Meta: "Google Analytics 4 · First user source / medium",
    sourceMedium: "Source / medium",
    selectAllSources: "Select all sources",
    deselectAllSources: "Deselect all sources",
    selectSource: "Select source",
    previous: "Previous period",
    adsBreakdown: "Conversions by platform",
    adsMeta: "Google Ads + Microsoft Advertising",
    source: "Source",
    clicks: "Clicks",
    impressions: "Impressions",
    cost: "Cost",
    cpa: "CPA",
    roas: "ROAS",
    noTrend: "No conversion series for the selected filters",
    noGa4Data: "GA4 returned no Key Events by source for this range",
    noAdsData: "No Ads conversions for this origin and range",
    note:
      "GA4 shows Key Events by first user source / medium. Google Ads and Microsoft Ads show conversions attributed by their own platforms; they remain separate to avoid double counting.",
  },
  technical: {
    seoScore: "SEO score",
    historicalCoreWebVitals: "Historical Core Web Vitals",
    lastSixMonths: "last 6 months",
    noCruxHistory: "CrUX has no sufficient history for this URL",
    accessibility: "Accessibility",
    bestPractices: "Best practices",
    technicalSeo: "Technical SEO",
    liveInspection: "Live inspection",
    httpStatus: "HTTP status",
    canonicalNotDetected: "Not detected",
    robotsNoRestrictions: "No restrictions",
    schemaNotDetected: "Not detected",
    responseTime: "Response time",
    microsoftCrawlIndex: "Crawl and index in Microsoft",
    noPageSpeedEquivalent: "Bing Webmaster · no PageSpeed equivalent",
    anchors: "Anchors",
    auditHistory: "Audit history",
    snapshots: "snapshots",
    futureAnalyses: "Future analyses will build this series",
    priorityActions: "Priority actions",
    reviewOpportunity: "Review opportunity",
    noCriticalOpportunities: "No critical opportunities.",
  },
  admin: {
    accessControl: "Access control",
    authorizedUsers: "Authorized users",
    invitationsHelp:
      "Each user signs in with their own Passkey. Invitations are one-time links and expire in seven days.",
    users: "users",
    directory: "Directory",
    directoryMeta: "Roles, status and registered Passkeys",
    user: "User",
    role: "Role",
    status: "Status",
    passkeys: "Passkeys",
    lastAccess: "Last access",
    viewer: "Viewer",
    admin: "Admin",
    active: "Active",
    disabled: "Disabled",
    invited: "Invited",
    renewInvitation: "Generate new invitation",
    enable: "Enable",
    disable: "Disable",
    inviteUser: "Invite user",
    inviteMeta: "Generate a Passkey registration link",
    name: "Name",
    namePlaceholder: "User name",
    emailPlaceholder: "user@company.com",
    generating: "Generating...",
    createInvitation: "Create invitation",
    oneTimeLink: "One-time link",
    copyInvitation: "Copy invitation",
    never: "Never",
  },
  devices: {
    personalAccess: "Personal access",
    myDevices: "My devices",
    help:
      "Register one Passkey per browser, device or security key you want to use to access the dashboard.",
    registeredPasskeys: "Registered Passkeys",
    synced: "synced",
    local: "local",
    created: "Created",
    lastUse: "Last use",
    empty: "There are no Passkeys registered for this account.",
    addDevice: "Add device",
    addMeta: "Generate a one-time link",
    linkHelp:
      "Copy the link and open it on the phone, laptop, browser or security key where you want to save the new Passkey.",
    generateLink: "Generate link",
    generating: "Generating...",
    oneTimeLink: "One-time link",
    expires: "expires",
    copyLink: "Copy link",
    linkReady: "Link ready to copy.",
    singleDevice: "Single-device",
    multiDevice: "Multi-device",
    deletionNote:
      "You can remove a Passkey from the Dashboard here. This does not delete the copy in your credential manager or close sessions that are already active.",
    remove: "Remove",
    removing: "Removing...",
    confirmDelete:
      "Remove the “{name}” Passkey from the Dashboard? It will no longer work for future sign-ins, but this will not delete it from your device manager or close active sessions.",
    confirmDeleteLast:
      "This is your last registered Passkey. Remove “{name}”? You will need an email code or another Passkey to sign in again. The local copy and active sessions will remain.",
    removed: "Passkey removed from the Dashboard.",
    never: "Never",
    fallbackError: "The link could not be generated.",
  },
  access: {
    newDevice: "New device",
    secureInvitation: "Secure invitation",
    addPasskey: "Add your Passkey",
    createPasskey: "Create your Passkey",
    validatingLink: "Validating link...",
    deviceName: "Device name",
    devicePlaceholder: "Laptop, phone or security key",
    creatingPasskey: "Creating Passkey...",
    createAndEnter: "Create Passkey and sign in",
    privateAccess: "Private access",
    loginHelp:
      "Use the Passkey saved in your device, password manager or security key.",
    verifying: "Verifying...",
    passkeyLogin: "Sign in with Passkey",
    emailCodeFallback: "I cannot use my Passkey",
    emailCodeHelp:
      "Enter your registered email and we will send you a temporary access code.",
    email: "Registered email",
    emailPlaceholder: "you@email.com",
    sendingCode: "Sending code...",
    sendCode: "Send code",
    emailCodeSent:
      "If the email belongs to an active user, you will receive a temporary code.",
    emailCodeVerifyHelp:
      "Enter the 6-digit code sent to your registered email.",
    code: "Code",
    verifyingCode: "Verifying code...",
    verifyCode: "Sign in with code",
    requestAnotherCode: "Request another code",
    backToPasskey: "Back to Passkey",
    emailCodeError: "The email code sign-in could not be completed.",
    unsupported: "This browser does not support Passkeys/WebAuthn.",
    secureFoot: "No passwords. Encrypted and revocable access.",
    linkError: "The link could not be opened.",
    cancelled: "The operation was cancelled or timed out.",
    authenticatorError:
      "Your authenticator could not sign in. Check that you are using the browser profile and device or password manager where you saved this passkey. To get in now, choose “I cannot use my Passkey” and request an email code; you can add another passkey later from My device.",
    authenticatorRegistrationError:
      "The browser or passkey manager rejected creation before sending it to the server. Check that you are on the official dashboard domain, unlock your credential manager, and try again. If this account already has a passkey in that manager, use it or try another device or manager. Technical detail:",
    genericPasskeyError: "The Passkey operation could not be completed.",
  },
};

const dictionaries: Record<LanguageCode, Dictionary> = { es, en };

interface LanguageContextValue {
  language: LanguageCode;
  locale: string;
  text: Dictionary;
  setLanguage: (language: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);
const STORAGE_KEY = "arenatours.analytics.language";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    if (typeof window === "undefined") return "es";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "en" || stored === "es" ? stored : "es";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      locale: dictionaries[language].locale,
      text: dictionaries[language],
      setLanguage: setLanguageState,
    }),
    [language],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const value = useContext(LanguageContext);
  if (!value) {
    throw new Error("useI18n must be used inside LanguageProvider");
  }
  return value;
}
